<template>
    <div id="header-cart" class="site-header__cart-area offcanvas">
        <div class="offcanvas__header">
            <h5 class="h3 offcanvas__title">Your Cart</h5>

            <a class="btn-close" href="#" data-ec-dismiss="offcanvas">
                <span class="visually-hidden">Close</span>
            </a>
        </div>

        <div class="offcanvas__body">
            <div v-if="cartItems.length === 0" class="cart-empty">
                You have nothing in your cart yet!
            </div>

            <div v-else>
                <!-- Warning if any unavailable -->
                <!--<div v-if="anyUnavailable" class="alert alert-warning mb-3">
                    <strong>Delivery Notice:</strong>
                    Some products in your cart are not available for delivery to your address.
                </div>-->

                <div class="cart-details" style="display: none !important">
                    <div class="cart-details__content">
                        <h5>Pickup</h5>
                        {{ pickupLocation }}
                    </div>
                    <div class="cart-details__action">
                        <a class="btn btn-outline-primary btn-sm" href="#">Change</a>
                    </div>
                </div>

                <div class="cart-row">
                    <div
                        v-for="(item, index) in cartItems"
                        :key="item.id"
                        class="cart-item"
                    >
                        <div class="cart-item__actions">
                            <span v-if="item.type === 'batch'" class="pill pill-secondary pill--small">Batch</span>

                            <button class="cart-item__actions__link" @click="removeItem(index)">
                                <svg focusable="false" role="img">
                                    <use xlink:href="/build/assets/icons.svg#trash" href="/build/assets/icons.svg#trash" />
                                </svg>
                            </button>
                        </div>

                        <figure class="cart-item__image" @click="goToProduct(item)" style="cursor: pointer;">
                            <img :src="item.image" alt="Product image"/>
                        </figure>

                        <div class="cart-item__details">
                            <div class="cart-item__details__info" @click="goToProduct(item)" style="cursor: pointer;">
                                <!-- Only the name now; pill is gone from here -->
                                <h4 class="cart-item__details__title">{{ item.name }}</h4>
                                <div class="cart-item__details__subtitle">{{ item.variant }}</div>
                                <div class="cart-item__details__footnote"> </div>
                                <!--<div
                                    v-if="!isAvailable(item)"
                                    class="delivery-unavailable-badge"
                                >
                                    Not available for delivery
                                </div>-->
                            </div>
                            <div class="cart-item__details__control">
                                <button
                                    type="button"
                                    class="number-decrease"
                                    @click="decreaseQuantity(item)"
                                >
                                    -
                                </button>
                                <input
                                    type="number"
                                    min="0"
                                    :max="item.max_quantity || 999"
                                    step="1"
                                    v-model.number="item.quantity"
                                    @input="enforceQuantityLimit(item)"
                                    @change="enforceQuantityLimit(item); saveCart()"
                                />
                                <button
                                    type="button"
                                    class="number-increase"
                                    :disabled="item.max_quantity && item.quantity >= item.max_quantity"
                                    @click="increaseQuantity(item)"
                                >
                                    +
                                </button>
                            </div>
                        </div>

                        <div class="cart-item__price">
                            {{ formatPrice(item.price * item.quantity) }}
                        </div>
                    </div>
                </div>

                <div
                    v-if="freeShippingProgress.enabled"
                    class="free-shipping-hint"
                    :class="{ 'free-shipping-hint--met': freeShippingProgress.met }"
                >
                    <template v-if="freeShippingProgress.met">
                        You've qualified for free shipping!
                    </template>
                    <template v-else>
                        Spend {{ formatPrice(freeShippingProgress.amountNeeded) }} more for free shipping.
                    </template>
                </div>

                <div class="cart-totals cart-totals--grandtotal">
                    <!-- Delivery Cost Row -->
                    <div class="cart-totals__row" v-if="false && selectedDeliveryOption && selectedDeliveryOption.id !== 'pickup'">
                        <div class="cart-totals__row__label">
                            Est. Delivery
                        </div>
                        <div class="cart-totals__row__value">
                            {{ formatPrice(deliveryCost) }}
                        </div>
                    </div>

                    <div class="cart-totals__row total-row">
                        <div class="cart-totals__row__label">Total</div>
                        <div class="cart-totals__row__value">{{ formatPrice(item_total) }}</div>
                    </div>
                    <div class="cart-totals__row gst-included">
                        <small class="text-muted"><nobr>includes {{ formatPrice(gst) }} GST</nobr></small>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="cartItems.length > 0" class="offcanvas__footer">
            <a class="btn btn-outline-primary btn-block btn-lg mb-2" href="#" data-ec-dismiss="offcanvas">
                Continue Shopping
            </a>
            <button class="btn btn-block btn-lg" @click="checkout">Begin Checkout</button>
        </div>
    </div>
</template>

<script setup>
import {onMounted, ref, computed, onUnmounted} from 'vue'
import {useCart} from '@/frontend/vue/composables/useCart'
// import {trackViewCart} from '@/frontend/vue/composables/useGTMTracking' // Commented out - may be added later

const {
    cartItems,
    subtotal,
    gst,
    total,
    item_total,
    deliveryCost,
    selectedDeliveryOption,
    deliveryConfig,
    freeShippingProgress,
    decreaseQuantity,
    increaseQuantity,
    removeFromCart,
    formatPrice,
    loadCart,
    saveCart
} = useCart()

// reactive cache of last API lookups
const lookupData = ref({})

function loadLookups() {
    try {
        lookupData.value = JSON.parse(localStorage.getItem('ec_addr_lookup') || '{}')
    } catch {
        lookupData.value = {}
    }
}

// check single item
function isAvailable(item) {
    // If delivery is turned off, always return true (item is available)
    if (deliveryConfig.value && !deliveryConfig.value.deliveryEnabled) {
        return true;
    }
    const key = item.handle || item.code
    const rec = lookupData.value[key]
    return rec?.available === true
}

// any / all helpers
const anyUnavailable = computed(() => {
    // If delivery is turned off, never show unavailable message
    if (deliveryConfig.value && !deliveryConfig.value.deliveryEnabled) {
        return false;
    }
    return cartItems.value.some(item => !isAvailable(item));
})

// Track view_cart when offcanvas opens
// COMMENTED OUT - May be added in the future
// function handleCartOpen() {
//     if (cartItems.value.length > 0) {
//         // Format cart items for tracking
//         const cartItemsArray = cartItems.value.map(item => ({
//             product_id: item.product_id || item.id,
//             product_name: item.product_name || item.name,
//             variant_name: item.variant_name || item.variant,
//             price: item.price,
//             quantity: item.quantity,
//         }));
//
//         trackViewCart(cartItemsArray, total.value);
//     }
// }

onMounted(() => {
    loadCart()
    loadLookups()

    // update when other tabs change it
    window.addEventListener('storage', loadLookups)
    // update when your first component fires this
    window.addEventListener('ec:availability-updated', loadLookups)

    // Track view_cart when the cart is opened
    // COMMENTED OUT - May be added in the future
    // window.addEventListener('cart:opened', handleCartOpen)
})

onUnmounted(() => {
    window.removeEventListener('storage', loadLookups)
    window.removeEventListener('ec:availability-updated', loadLookups)
    // window.removeEventListener('cart:opened', handleCartOpen) // Commented out - may be added later
})

function removeItem(index) {
    removeFromCart(index)
}

function goToProduct(item) {
    window.location.href = item.type === 'batch' ? `/specials` : `/products/${item.handle}`
}

function enforceQuantityLimit(item) {
    if (item.max_quantity && item.quantity > item.max_quantity) {
        item.quantity = item.max_quantity
    }
    if (item.quantity < 0) {
        item.quantity = 0
    }
}

function checkout() {
    saveCart((data) => {
        window.location = '/checkout/';
    })
}
</script>

<style scoped>
.cart-item__actions__link {
    background: none;
    border: none;
    cursor: pointer;
}

/* Needed to come up with a way to show its a batch/special - this will do for now */
.pill--small {
    font-size: 0.8rem;       /* ~10px on a 16px root */
    padding: 0.3em 0.6em;    /* tighter padding */
    line-height: 1;
    margin-top: -2px;
    margin-right: 5px;
}

/* ensure the pill sits nicely beside the trash button */
.cart-item__actions {
    display: flex;
    align-items: center;
}

.cart-item__actions__link + .pill--small {
    margin-left: 0.4em;
}

.gst-included {
    justify-content: end; font-weight: normal; color: #333; margin-top: 5px;
}

.delivery-unavailable-badge {
    display: inline-block;
    font-size: 0.75rem;
    background-color: #dc3545;
    color: #fff;
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    margin-top: 0.5rem;
}

.cart-totals__row__subtext {
    font-weight: normal;
    color: #666;
    margin-top: 2px;
}

.alert { position: relative; padding: 0.75rem 1.25rem; border-radius: 0.25rem; }
.alert-warning {
    color: #856404;
    background-color: #fff3cd;
    border-color: #ffeeba;
    margin-bottom: 1.5rem;
}

.site-header__cart-area .cart-totals {  margin-top: 20px;
    border-top: 1px solid #ddd;
    padding-top: 15px; }

.total-row {
    font-weight: bold;
    font-size: 1.1em;

}

.mb-3 {
    margin-bottom: 1rem;
}

.free-shipping-hint {
    margin-top: 15px;
    padding: 10px 12px;
    border-radius: 6px;
    background-color: #fff3cd;
    color: #856404;
    font-size: 0.9rem;
    text-align: center;
}

.free-shipping-hint--met {
    background-color: #d4edda;
    color: #155724;
}

</style>
